/**
 * 従業員タグAPI クライアント
 */
import { parseApiError } from "../utils/apiError";
import { authFetch, API_BASE_URL } from "./client";

export interface EmployeeTagResponse {
    id: number;
    organization_id: number | null;
    name: string | null;
    description: string | null;
    color: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface EmployeeTagListResponse {
    items: EmployeeTagResponse[];
    total: number;
}

export interface EmployeeTagCreateRequest {
    organization_id?: number | null;
    name?: string | null;
    description?: string | null;
    color?: string | null;
}

export interface EmployeeTagUpdateRequest {
    organization_id?: number | null;
    name?: string | null;
    description?: string | null;
    color?: string | null;
}

export interface EmployeeTagAssignmentResponse {
    employee_id: number;
    tag_ids: number[];
}

/** 従業員タグマスタ一覧 */
export async function fetchEmployeeTags(): Promise<EmployeeTagListResponse> {
    const response = await authFetch(`${API_BASE_URL}/employee-tags`);
    if (!response.ok) {
        throw new Error(`Failed to fetch employee tags: ${response.status}`);
    }
    return response.json();
}

/** 従業員タグを作成 */
export async function createEmployeeTag(
    data: EmployeeTagCreateRequest,
): Promise<EmployeeTagResponse> {
    const response = await authFetch(`${API_BASE_URL}/employee-tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, "タグの作成に失敗しました");
    }
    return response.json();
}

/** 従業員タグを更新 */
export async function updateEmployeeTag(
    tagId: number,
    data: EmployeeTagUpdateRequest,
): Promise<EmployeeTagResponse> {
    const response = await authFetch(`${API_BASE_URL}/employee-tags/${tagId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, "タグの更新に失敗しました");
    }
    return response.json();
}

/** 従業員タグを削除（論理削除） */
export async function deleteEmployeeTag(tagId: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/employee-tags/${tagId}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw await parseApiError(response, "タグの削除に失敗しました");
    }
}

/** 従業員に紐づくタグIDリストを取得 */
export async function fetchEmployeeTagAssignments(
    employeeId: number,
): Promise<EmployeeTagAssignmentResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/employees/${employeeId}/tags`,
    );
    if (!response.ok) {
        throw new Error(`Failed to fetch employee tag assignments: ${response.status}`);
    }
    return response.json();
}

/** 従業員のタグ紐づけを一括更新 */
export async function updateEmployeeTagAssignments(
    employeeId: number,
    tagIds: number[],
): Promise<EmployeeTagAssignmentResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/employees/${employeeId}/tags`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tag_ids: tagIds }),
        },
    );
    if (!response.ok) {
        throw await parseApiError(response, "タグの紐づけ更新に失敗しました");
    }
    return response.json();
}
